import React, { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { cloneDeep, set, get, last } from "lodash";
import { useForm } from "react-hook-form";

import useTranslations from "../../hooks/useTranslations";
import {
  H5,
} from "../wombat-ui";

import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
} from '@chakra-ui/react';

import AutoComplete from "../wombat-ui/components/Form/Input/AutoComplete";
import { QuestionFormInput } from "./QuestionWithOptionsForm";
import { SectionFormInput } from "./SectionForm";
import useViewer from "../../hooks/useViewer";
import { Tag } from "@chakra-ui/react";
import { ProfessionFragment, useGetProfessionsLazyQuery } from '../../graphql/generated/definitions';

type JobSectionModalProps = {
  open: boolean;
  onClose: () => void;
  getValues: ReturnType<typeof useForm>["getValues"];
  setValue: ReturnType<typeof useForm>["setValue"];
  fields: any;
  insertIndex: number;
  name: string;
  reset: ReturnType<typeof useForm>["reset"];
};

const getDefaultSectionAttribute = ({
  index = 0,
  type,
}): Partial<SectionFormInput> => ({
  order: index,
  title: "",
  description: "",
  type,
});

export const getDefaultQuestionAttribute = ({
  index = 0,
  type,
  options,
  skill,
  skillId,
}): Partial<QuestionFormInput> => ({
  order: index,
  label: skill?.name || "",
  options,
  reportTypes: ["FirstReport", "IntermediateReport", "FinalReport"],
  skill: skill || null,
  skillId: skillId || null,
  type,
});

export const JobSectionModal: React.FC<JobSectionModalProps> = ({
  open,
  onClose,
  getValues,
  fields,
  insertIndex,
  name,
  reset,
}) => {
  const t = useTranslations();
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedSkills, setSelectedSkills] = useState({});
  const { locale } = useIntl();
  const {
    company: { questionOptionsConfigurations },
  } = useViewer();
  const questionOptionsConfiguration = questionOptionsConfigurations.find(
    (a) => a.locale === locale
  );
  
  const handleClose = useCallback(() => {
    setSelectedJob(null);
    setSelectedSkills({});
    onClose();
  }, [onClose]);

  const getLatestQuestion = useCallback(
    (startAt: number, searchWithin: Record<string, any>) => {
      let lastQuestion;
      let previousSectionIndex = startAt - 1;

      // Guard to avoid unecessary lookup
      if (previousSectionIndex < 0) return [];
      // Loop on every sections trying to find one that does have a question
      while (lastQuestion === undefined && previousSectionIndex >= 0) {
        lastQuestion = last(
          get(
            searchWithin,
            `templateAttributes.sectionsAttributes[${previousSectionIndex}]`
          )?.questionsAttributes
        );

        previousSectionIndex -= 1;
      }

      return lastQuestion;
    },
    []
  );

  const createSection = useCallback(() => {
    let index = 0;
    const values = cloneDeep(getValues());
    const sectionsAttributesName = `${name}.sectionsAttributes`;
    const sectionsAttributes =
      get(values, sectionsAttributesName) || [];
    
    sectionsAttributes
      .splice(
        insertIndex === -1 ? sectionsAttributes.length : insertIndex,
        0,
        {
          ...getDefaultSectionAttribute({
            index: fields.length,
          }),
          title: t("templates.jobsSectionDefaultTitle", {
            profession: selectedJob.name,
          }),
          description: t("templates.jobsSectionDefaultDescription", {
            profession: selectedJob.name,
          }),
          questionsAttributes: [
            ...Object.keys(selectedSkills)
              .map((skillId) => {
                if (selectedSkills[skillId]) {
                  return getDefaultQuestionAttribute({
                    type: "QuestionSkill",
                    index: index++,
                    options: questionOptionsConfiguration.skillQuestionOptionsConfiguration.map(
                      (e) => ({
                        value: e.value,
                        label: e.label,
                      })
                    ),
                    skill: selectedJob.skills.find((a) => a.id === skillId),
                    skillId: skillId,
                  });
                }
              })
              .filter((a) => a),
          ],
        }
      )
      .map((i, index) => ({ ...i, index }));

    set(values, sectionsAttributesName, sectionsAttributes);

    reset(values);
    handleClose();
  }, [getValues, handleClose, reset, insertIndex, name, selectedSkills]);

  const toggleSkillSelection = useCallback(
    (skill) => {
      setSelectedSkills({ ...selectedSkills, [skill]: !selectedSkills[skill] });
    },
    [selectedSkills]
  );

  const selectAllSkills = useCallback(
    (skills) => {
      const skillMap = {};
      skills?.map((skill) => (skillMap[skill.id] = true));
      setSelectedSkills(skillMap);
    },
    [selectedSkills]
  );

  const deselectAllSkills = useCallback(
    (skills) => {
      const skillMap = {};
      skills.map((skill) => (skillMap[skill.id] = false));
      setSelectedSkills(skillMap);
    },
    [selectedSkills]
  );

  return (
    <Modal
      isOpen={open}
      aria-label="Template form element creation modal"
      onClose={handleClose}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t("templates.createJobSection")}
          <p className="text-xs font-light text-light-900">
            {t("templates.jobSectionModalDescription")}
          </p>
        </ModalHeader>
        <ModalBody>
          <div className="pt-2">
            <AutoComplete
              size="medium"
              id={`searchJobInput`}
              listStrict
              query={{
                hook: useGetProfessionsLazyQuery,
                accessKey: "professions",
                hookOptions: { locale, sortBy: "name" },
              }}
              onChange={(e) => {
                setSelectedJob(e);
                selectAllSkills(e.skills);
              }}
              getValue={(i: ProfessionFragment) => i.id}
              getLabel={(i: ProfessionFragment) => i.name}
              renderItem={(i: ProfessionFragment) => i.name}
              placeholder={t("templates.pickJobForSection") as string}
            />
            {selectedJob &&
              (selectedJob.skills?.length > 0 ? (
                <>
                  <div className="pt-4">
                    <H5 className="text-gray-900">
                      {t("templates.relatedSkills")}
                    </H5>
                  </div>
                  <div className="flex py-4 space-x-4">
                    <Button
                      variant="link"
                      scale="inline"
                      onClick={() => selectAllSkills(selectedJob.skills)}
                    >
                      {t("templates.jobSkillsSelectAll")}
                    </Button>
                    <Button
                      variant="link"
                      scale="inline"
                      onClick={() => deselectAllSkills(selectedJob.skills)}
                    >
                      {t("templates.jobSkillsUnselectAll")}
                    </Button>
                  </div>
                  <div className="flex flex-wrap -mx-1.5">
                    {selectedJob.skills.map((skill) => (
                      <div className="px-1.5 py-1" key={skill.id}>
                        <Tag
                          variant={selectedSkills[skill.id] ? "solid" : "outline"}
                          role="button"
                          onClick={() => toggleSkillSelection(skill.id)}
                          data-test-id={`badge_${skill.id}`}
                        >
                          {skill.name}
                        </Tag>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                t("templates.noSkillsFound")
              ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button
              onClick={handleClose}
            >
              {t("forms.actions.cancel")}
            </Button>

            <Spacer />

            <Button
              disabled={
                !selectedJob ||
                Object.keys(selectedSkills).length === 0 ||
                Object.keys(selectedSkills).every((k) => !selectedSkills[k])
              }
              onClick={createSection}
              id="addQuestionSkills"
              colorScheme="blue"
            >
              {t("templates.addNQuestions", {
                count: Object.keys(selectedSkills).filter((k) => selectedSkills[k])
                  .length,
              })}
            </Button>

          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
